import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ApolloError } from '@apollo/client';

import {
  useZoomSyncAccountsMutation,
  useZoomSyncRecordingsMutation,
  useZoomRefreshAllMutation,
  useZoomSyncAllRecordingsMutation,
  useZoomSyncUsageMutation,
  useZoomRunAutoMatchingMutation,
  useZoomSetGroupAccountMutation,
  useZoomSetGroupScheduleMutation,
  GroupZoomScheduleSlotInput,
  useZoomCreateManualMatchMutation,
  useZoomUnlinkRecordingMutation,
  useZoomMarkMissingMutation,
  useZoomRetryRecordingMutation,
  useZoomReprocessAiMutation,
  useZoomDeleteRecordingMutation,
  useZoomDeleteRecordingsMutation,
  useZoomSendMorningReportMutation,
  CustomErrorType,
  Maybe,
} from 'core/graphql';
import { GlobalToastTypeNames, ToastTypes } from 'types';

import { useToasts } from '../Toast';

type CustomErrors = Maybe<Array<Maybe<CustomErrorType>>> | undefined;

interface IMutationPayload {
  success?: Maybe<boolean>;
  customErrors?: CustomErrors;
}

const isPermissionError = (error?: ApolloError): boolean =>
  Boolean(
    error?.graphQLErrors?.some((gqlError) =>
      /permission/i.test(gqlError.message),
    ),
  );

const firstCustomError = (errors: CustomErrors): string | undefined =>
  errors?.find((entry) => Boolean(entry?.message))?.message ?? undefined;

/**
 * Facade over the 11 Zoom mutations. Every operation returns `{ run, loading }`;
 * `run` shows a success/error toast and resolves with the mutation payload so
 * callers can decide whether to refetch.
 */
export const useZoomMutations = () => {
  const { t } = useTranslation();
  const { addToast } = useToasts();

  const notifySuccess = useCallback(
    (text?: string) => {
      addToast({
        data: {
          type: ToastTypes.success,
          title: `${t('pages.zoom.toast.successTitle')}`,
          text,
        },
        type: GlobalToastTypeNames.ZoomMutation,
      });
    },
    [addToast, t],
  );

  const notifyError = useCallback(
    (text: string, permission = false) => {
      addToast({
        data: {
          type: ToastTypes.error,
          title: permission
            ? `${t('pages.zoom.toast.noAccessTitle')}`
            : `${t('pages.zoom.toast.errorTitle')}`,
          text,
        },
        type: GlobalToastTypeNames.ZoomError,
      });
    },
    [addToast, t],
  );

  /** Inspects a mutation payload and surfaces the right toast. */
  const reportPayload = useCallback(
    (payload?: Maybe<IMutationPayload>, successText?: string): boolean => {
      const customError = firstCustomError(payload?.customErrors);

      if (customError) {
        notifyError(customError);
        return false;
      }

      if (payload?.success === false) {
        notifyError(`${t('pages.zoom.toast.failed')}`);
        return false;
      }

      notifySuccess(successText);
      return true;
    },
    [notifyError, notifySuccess, t],
  );

  const reportApolloError = useCallback(
    (error: unknown) => {
      const apolloError = error instanceof ApolloError ? error : undefined;
      const permission = isPermissionError(apolloError);
      notifyError(
        apolloError?.message ?? `${t('pages.zoom.toast.unknownError')}`,
        permission,
      );
    },
    [notifyError, t],
  );

  const [syncAccountsFn, syncAccountsState] = useZoomSyncAccountsMutation();
  const [
    syncRecordingsFn,
    syncRecordingsState,
  ] = useZoomSyncRecordingsMutation();
  const [refreshAllFn, refreshAllState] = useZoomRefreshAllMutation();
  const [
    syncAllRecordingsFn,
    syncAllRecordingsState,
  ] = useZoomSyncAllRecordingsMutation();
  const [syncUsageFn, syncUsageState] = useZoomSyncUsageMutation();
  const [runMatchingFn, runMatchingState] = useZoomRunAutoMatchingMutation();
  const [
    setGroupAccountFn,
    setGroupAccountState,
  ] = useZoomSetGroupAccountMutation();
  const [
    setGroupScheduleFn,
    setGroupScheduleState,
  ] = useZoomSetGroupScheduleMutation();
  const [manualMatchFn, manualMatchState] = useZoomCreateManualMatchMutation();
  const [unlinkFn, unlinkState] = useZoomUnlinkRecordingMutation();
  const [markMissingFn, markMissingState] = useZoomMarkMissingMutation();
  const [retryFn, retryState] = useZoomRetryRecordingMutation();
  const [reprocessAiFn, reprocessAiState] = useZoomReprocessAiMutation();
  const [
    deleteRecordingFn,
    deleteRecordingState,
  ] = useZoomDeleteRecordingMutation();
  const [
    deleteRecordingsFn,
    deleteRecordingsState,
  ] = useZoomDeleteRecordingsMutation();
  const [sendReportFn, sendReportState] = useZoomSendMorningReportMutation();

  const syncAccounts = useCallback(async (): Promise<boolean> => {
    try {
      const res = await syncAccountsFn();
      return reportPayload(
        res.data?.syncZoomAccounts,
        `${t('pages.zoom.toast.queued')}`,
      );
    } catch (error) {
      reportApolloError(error);
      return false;
    }
  }, [syncAccountsFn, reportPayload, reportApolloError, t]);

  const refreshAll = useCallback(
    async (dateFrom?: string, dateTo?: string): Promise<boolean> => {
      try {
        const res = await refreshAllFn({
          variables: { dateFrom: dateFrom ?? null, dateTo: dateTo ?? null },
        });
        return reportPayload(
          res.data?.refreshAllZoomData,
          `${t('pages.zoom.toast.queued')}`,
        );
      } catch (error) {
        reportApolloError(error);
        return false;
      }
    },
    [refreshAllFn, reportPayload, reportApolloError, t],
  );

  const syncAllRecordings = useCallback(
    async (dateFrom?: string, dateTo?: string): Promise<boolean> => {
      try {
        const res = await syncAllRecordingsFn({
          variables: { dateFrom: dateFrom ?? null, dateTo: dateTo ?? null },
        });
        return reportPayload(
          res.data?.syncAllZoomRecordings,
          `${t('pages.zoom.toast.queued')}`,
        );
      } catch (error) {
        reportApolloError(error);
        return false;
      }
    },
    [syncAllRecordingsFn, reportPayload, reportApolloError, t],
  );

  const syncUsage = useCallback(async (): Promise<boolean> => {
    try {
      const res = await syncUsageFn();
      return reportPayload(
        res.data?.syncZoomUsage,
        `${t('pages.zoom.toast.queued')}`,
      );
    } catch (error) {
      reportApolloError(error);
      return false;
    }
  }, [syncUsageFn, reportPayload, reportApolloError, t]);

  const syncRecordings = useCallback(
    async (date: string): Promise<boolean> => {
      try {
        const res = await syncRecordingsFn({ variables: { date } });
        return reportPayload(
          res.data?.syncZoomRecordings,
          `${t('pages.zoom.toast.queued')}`,
        );
      } catch (error) {
        reportApolloError(error);
        return false;
      }
    },
    [syncRecordingsFn, reportPayload, reportApolloError, t],
  );

  const runMatching = useCallback(
    async (date: string): Promise<boolean> => {
      try {
        const res = await runMatchingFn({ variables: { date } });
        return reportPayload(
          res.data?.runAutoMatching,
          `${t('pages.zoom.toast.queued')}`,
        );
      } catch (error) {
        reportApolloError(error);
        return false;
      }
    },
    [runMatchingFn, reportPayload, reportApolloError, t],
  );

  const setGroupAccount = useCallback(
    async (groupId: string, zoomAccountId?: string): Promise<boolean> => {
      try {
        const res = await setGroupAccountFn({
          variables: { groupId, zoomAccountId: zoomAccountId ?? null },
        });
        return reportPayload(res.data?.setGroupZoomAccount);
      } catch (error) {
        reportApolloError(error);
        return false;
      }
    },
    [setGroupAccountFn, reportPayload, reportApolloError],
  );

  const setGroupSchedule = useCallback(
    async (
      groupId: string,
      zoomAccountId: string | undefined,
      slots: GroupZoomScheduleSlotInput[],
    ): Promise<boolean> => {
      try {
        const res = await setGroupScheduleFn({
          variables: { groupId, zoomAccountId: zoomAccountId ?? null, slots },
        });
        return reportPayload(res.data?.setGroupZoomSchedule);
      } catch (error) {
        reportApolloError(error);
        return false;
      }
    },
    [setGroupScheduleFn, reportPayload, reportApolloError],
  );

  const manualMatch = useCallback(
    async (lessonId: string, zoomRecordingId: string): Promise<boolean> => {
      try {
        const res = await manualMatchFn({
          variables: { lessonId, zoomRecordingId },
        });
        return reportPayload(res.data?.createManualLessonRecording);
      } catch (error) {
        reportApolloError(error);
        return false;
      }
    },
    [manualMatchFn, reportPayload, reportApolloError],
  );

  const unlink = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const res = await unlinkFn({ variables: { id } });
        return reportPayload(res.data?.unlinkLessonRecording);
      } catch (error) {
        reportApolloError(error);
        return false;
      }
    },
    [unlinkFn, reportPayload, reportApolloError],
  );

  const markMissing = useCallback(
    async (lessonId: string): Promise<boolean> => {
      try {
        const res = await markMissingFn({ variables: { lessonId } });
        return reportPayload(res.data?.markLessonMissingRecording);
      } catch (error) {
        reportApolloError(error);
        return false;
      }
    },
    [markMissingFn, reportPayload, reportApolloError],
  );

  const retry = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const res = await retryFn({ variables: { id } });
        return reportPayload(
          res.data?.retryLessonRecording,
          `${t('pages.zoom.toast.queued')}`,
        );
      } catch (error) {
        reportApolloError(error);
        return false;
      }
    },
    [retryFn, reportPayload, reportApolloError, t],
  );

  const reprocessAi = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const res = await reprocessAiFn({ variables: { id } });
        return reportPayload(
          res.data?.reprocessAi,
          `${t('pages.zoom.toast.queued')}`,
        );
      } catch (error) {
        reportApolloError(error);
        return false;
      }
    },
    [reprocessAiFn, reportPayload, reportApolloError, t],
  );

  const deleteRecording = useCallback(
    async (id: string, confirm: string): Promise<boolean> => {
      try {
        const res = await deleteRecordingFn({ variables: { id, confirm } });
        return reportPayload(res.data?.deleteZoomRecording);
      } catch (error) {
        reportApolloError(error);
        return false;
      }
    },
    [deleteRecordingFn, reportPayload, reportApolloError],
  );

  /**
   * Пакетное удаление (ТЗ 0043). Возвращает число реально поставленных в
   * очередь: пакет не падает целиком из-за одной проблемной строки, поэтому
   * вызывающему важно не «да/нет», а сколько ушло.
   */
  const deleteRecordings = useCallback(
    async (ids: string[]): Promise<number> => {
      try {
        const res = await deleteRecordingsFn({
          variables: { ids, confirm: true },
        });
        const payload = res.data?.deleteZoomRecordings;
        if (!reportPayload(payload)) {
          return 0;
        }
        return payload?.queued ?? 0;
      } catch (error) {
        reportApolloError(error);
        return 0;
      }
    },
    [deleteRecordingsFn, reportPayload, reportApolloError],
  );

  const sendMorningReport = useCallback(
    async (date?: string): Promise<boolean> => {
      try {
        const res = await sendReportFn({
          variables: { date: date ?? null },
        });
        return reportPayload(
          res.data?.sendMorningReport,
          `${t('pages.zoom.toast.reportSent')}`,
        );
      } catch (error) {
        reportApolloError(error);
        return false;
      }
    },
    [sendReportFn, reportPayload, reportApolloError, t],
  );

  return {
    syncAccounts: { run: syncAccounts, loading: syncAccountsState.loading },
    refreshAll: { run: refreshAll, loading: refreshAllState.loading },
    syncAllRecordings: {
      run: syncAllRecordings,
      loading: syncAllRecordingsState.loading,
    },
    syncUsage: { run: syncUsage, loading: syncUsageState.loading },
    syncRecordings: {
      run: syncRecordings,
      loading: syncRecordingsState.loading,
    },
    runMatching: { run: runMatching, loading: runMatchingState.loading },
    setGroupAccount: {
      run: setGroupAccount,
      loading: setGroupAccountState.loading,
    },
    setGroupSchedule: {
      run: setGroupSchedule,
      loading: setGroupScheduleState.loading,
    },
    manualMatch: { run: manualMatch, loading: manualMatchState.loading },
    unlink: { run: unlink, loading: unlinkState.loading },
    markMissing: { run: markMissing, loading: markMissingState.loading },
    retry: { run: retry, loading: retryState.loading },
    reprocessAi: { run: reprocessAi, loading: reprocessAiState.loading },
    deleteRecording: {
      run: deleteRecording,
      loading: deleteRecordingState.loading,
    },
    deleteRecordings: {
      run: deleteRecordings,
      loading: deleteRecordingsState.loading,
    },
    sendMorningReport: {
      run: sendMorningReport,
      loading: sendReportState.loading,
    },
  };
};
